import React, { useEffect, useState } from "react";
import { apiFetch, getApiBase, parseJsonOrExplain } from "./apiBase";

export default function AdminUsersPage({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [deleteBusyId, setDeleteBusyId] = useState(null);
  const [roleBusyId, setRoleBusyId] = useState(null);
  const [error, setError] = useState(null);
  const [ok, setOk] = useState(null);
  const PRIMARY_ADMIN_EMAIL = "erik.hogstrom@peqab.se";

  const loadUsers = () => {
    setError(null);
    apiFetch(`${getApiBase()}/admin/users`)
      .then((r) => parseJsonOrExplain(r))
      .then((data) => setUsers(Array.isArray(data) ? data : []))
      .catch((e) => setError(e.message || "Kunde inte hämta användare"));
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const submit = (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setOk(null);
    apiFetch(`${getApiBase()}/admin/users`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: email.trim().toLowerCase() }),
    })
      .then((r) => parseJsonOrExplain(r))
      .then(() => {
        setOk("Inbjudan skapad. Om SMTP saknas: kontrollera backend-loggen för länken.");
        setEmail("");
        loadUsers();
      })
      .catch((e) => setError(e.message || "Kunde inte bjuda in användare"))
      .finally(() => setBusy(false));
  };

  const removeUser = (user) => {
    if (!window.confirm(`Ta bort användaren ${user.email}?`)) return;
    setError(null);
    setOk(null);
    setDeleteBusyId(user.id);
    apiFetch(`${getApiBase()}/admin/users/${user.id}`, { method: "DELETE" })
      .then((r) => parseJsonOrExplain(r))
      .then(() => {
        setOk(`Användaren ${user.email} togs bort.`);
        loadUsers();
      })
      .catch((e) => setError(e.message || "Kunde inte ta bort användare"))
      .finally(() => setDeleteBusyId(null));
  };

  const updateUserRole = (user, role) => {
    const nextRole = String(role || "").trim().toLowerCase();
    if (!["admin", "user"].includes(nextRole)) return;
    if (String(user.role || "").toLowerCase() === nextRole) return;
    setError(null);
    setOk(null);
    setRoleBusyId(user.id);
    apiFetch(`${getApiBase()}/admin/users/${user.id}/role`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ role: nextRole }),
    })
      .then((r) => parseJsonOrExplain(r))
      .then(() => {
        setOk(`Roll uppdaterad för ${user.email} (${nextRole}).`);
        loadUsers();
      })
      .catch((e) => setError(e.message || "Kunde inte uppdatera roll"))
      .finally(() => setRoleBusyId(null));
  };

  return (
    <section
      style={{
        background: "#fbfaf7",
        border: "1px solid rgba(17,17,17,0.10)",
        borderRadius: "22px",
        padding: "24px",
      }}
    >
      <h2 style={{ margin: 0, fontSize: "30px", fontWeight: 500 }}>Admin - användare</h2>
      <p style={{ color: "#5f5a52", fontFamily: "Arial, Helvetica, sans-serif" }}>
        Bjud in användare via e-post. De får en länk för att skapa lösenord.
      </p>

      <form onSubmit={submit} style={{ display: "flex", gap: "10px", marginBottom: "16px" }}>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="namn@foretag.se"
          style={{
            flex: 1,
            padding: "10px 12px",
            borderRadius: "10px",
            border: "1px solid rgba(17,17,17,0.2)",
          }}
        />
        <button
          type="submit"
          disabled={busy}
          style={{
            padding: "10px 16px",
            borderRadius: "999px",
            border: "none",
            background: "#111",
            color: "#fff",
            cursor: busy ? "wait" : "pointer",
          }}
        >
          {busy ? "Skickar..." : "Bjud in"}
        </button>
      </form>

      {error && <p style={{ color: "#b42318", fontFamily: "Arial, Helvetica, sans-serif" }}>{error}</p>}
      {ok && <p style={{ color: "#0a7f5a", fontFamily: "Arial, Helvetica, sans-serif" }}>{ok}</p>}

      <div style={{ overflowX: "auto", background: "#fff", borderRadius: "12px", border: "1px solid rgba(17,17,17,0.1)" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "Arial, Helvetica, sans-serif", fontSize: "14px" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid rgba(17,17,17,0.12)" }}>
              <th style={{ textAlign: "left", padding: "10px 12px" }}>E-post</th>
              <th style={{ textAlign: "left", padding: "10px 12px" }}>Roll</th>
              <th style={{ textAlign: "left", padding: "10px 12px" }}>Status</th>
              <th style={{ textAlign: "left", padding: "10px 12px" }}>Åtgärd</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} style={{ borderBottom: "1px solid rgba(17,17,17,0.06)" }}>
                <td style={{ padding: "10px 12px" }}>{u.email}</td>
                <td style={{ padding: "10px 12px" }}>
                  {String(u.email || "").toLowerCase() === PRIMARY_ADMIN_EMAIL ? (
                    <span title="Primär admin kan inte ändras">admin</span>
                  ) : (
                    <select
                      value={String(u.role || "user")}
                      disabled={roleBusyId === u.id}
                      onChange={(e) => updateUserRole(u, e.target.value)}
                      style={{
                        padding: "6px 8px",
                        borderRadius: "8px",
                        border: "1px solid rgba(17,17,17,0.2)",
                        background: "#fff",
                        fontSize: "12px",
                      }}
                    >
                      <option value="user">user</option>
                      <option value="admin">admin</option>
                    </select>
                  )}
                </td>
                <td style={{ padding: "10px 12px" }}>
                  {u.has_password ? "Aktiv" : "Inbjuden (ej satt lösenord)"}
                </td>
                <td style={{ padding: "10px 12px" }}>
                  {currentUser && Number(currentUser.id) !== Number(u.id) ? (
                    <button
                      type="button"
                      disabled={deleteBusyId === u.id}
                      onClick={() => removeUser(u)}
                      style={{
                        padding: "6px 10px",
                        borderRadius: "999px",
                        border: "1px solid rgba(180,35,24,0.4)",
                        background: "#fff",
                        color: "#b42318",
                        cursor: deleteBusyId === u.id ? "wait" : "pointer",
                        fontSize: "12px",
                      }}
                    >
                      {deleteBusyId === u.id ? "Tar bort..." : "Ta bort"}
                    </button>
                  ) : (
                    <span style={{ color: "#9a9288", fontSize: "12px" }}>-</span>
                  )}
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr>
                <td style={{ padding: "12px" }} colSpan={4}>Inga användare hittades.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
